import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Bell,
  Check,
  Edit3,
  Ellipsis,
  LogOut,
  Mail,
  Menu,
  Plus,
  Trash2,
  UserPlus,
  Users,
} from 'lucide-react';
import Alert from '../components/common/Alert';
import Badge from '../components/common/Badge';
import BrandMark from '../components/common/BrandMark';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import ConfirmDialog from '../components/common/ConfirmDialog';
import Drawer from '../components/common/Drawer';
import DropdownMenu from '../components/common/DropdownMenu';
import EmptyState from '../components/common/EmptyState';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Modal from '../components/common/Modal';
import Tabs from '../components/common/Tabs';
import Tooltip from '../components/common/Tooltip';
import Checkbox from '../components/forms/Checkbox';
import Input from '../components/forms/Input';
import RadioButton from '../components/forms/RadioButton';
import SearchInput from '../components/forms/SearchInput';
import Select from '../components/forms/Select';
import Textarea from '../components/forms/Textarea';
import Pagination from '../components/tables/Pagination';
import Table from '../components/tables/Table';
import { userService } from '../services';

function SectionTitle({ eyebrow, title, description }) {
  return (
    <div className="mb-5">
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary-700">{eyebrow}</p>
      <h2 className="mt-1 text-xl font-semibold tracking-tight text-content-primary">{title}</h2>
      {description && <p className="mt-1 text-sm text-content-muted">{description}</p>}
    </div>
  );
}

function ComponentsPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('resumen');
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState({ key: 'name', direction: 'asc' });
  const [alertVisible, setAlertVisible] = useState(true);
  const [people, setPeople] = useState([]);
  const { register, handleSubmit, formState: { errors } } = useForm();

  useEffect(() => {
    let mounted = true;
    userService.getAll()
      .then((response) => {
        if (!mounted) return;
        setPeople(response.data.map((user) => ({
          id: user.id,
          name: user.name,
          role: user.roleLabel,
          zone: user.zone,
          status: user.status === 'active' ? 'Activo' : user.status === 'pending' ? 'Pendiente' : 'Inactivo',
        })));
      })
      .catch(() => mounted && setPeople([]));
    return () => { mounted = false; };
  }, []);

  const sortedPeople = useMemo(() => {
    const filtered = people.filter((person) =>
      Object.values(person).some((value) => value.toString().toLowerCase().includes(query.toLowerCase()))
    );
    return [...filtered].sort((a, b) => {
      const result = a[sort.key].localeCompare(b[sort.key]);
      return sort.direction === 'asc' ? result : -result;
    });
  }, [people, query, sort]);

  const handleSort = (key) => {
    setSort((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const columns = [
    {
      key: 'name',
      header: 'Nombre',
      sortable: true,
      render: (value) => <span className="font-medium text-content-primary">{value}</span>,
    },
    { key: 'role', header: 'Rol', sortable: true },
    { key: 'zone', header: 'Zona', sortable: true },
    {
      key: 'status',
      header: 'Estado',
      render: (value) => (
        <Badge variant={value === 'Activo' ? 'success' : value === 'Pendiente' ? 'warning' : 'neutral'} dot>
          {value}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: () => (
        <button className="rounded-md p-1.5 text-content-muted hover:bg-slate-100" aria-label="Más acciones">
          <Ellipsis className="size-4" />
        </button>
      ),
    },
  ];

  const tabs = [
    { id: 'resumen', label: 'Resumen', content: <p className="text-sm text-content-secondary">Vista general de la estructura y sus responsables.</p> },
    { id: 'actividad', label: 'Actividad', count: 8, content: <p className="text-sm text-content-secondary">Los movimientos recientes aparecerán en esta sección.</p> },
    { id: 'permisos', label: 'Permisos', content: <p className="text-sm text-content-secondary">Control de acceso asignado al perfil seleccionado.</p> },
  ];

  return (
    <div className="min-h-screen bg-surface-page">
      <header className="sticky top-0 z-30 border-b bg-slate-950/95 text-white backdrop-blur">
        <div className="mx-auto flex h-16 max-w-content items-center justify-between px-container-x">
          <BrandMark />
          <div className="flex items-center gap-3">
            <Badge className="hidden sm:inline-flex">Sistema visual</Badge>
            <Tooltip content="Notificaciones">
              <button className="grid size-9 place-items-center rounded-control text-slate-400 hover:bg-white/10 hover:text-white" aria-label="Notificaciones">
                <Bell className="size-4" />
              </button>
            </Tooltip>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-content px-container-x py-10">
        <div className="flex flex-col justify-between gap-6 border-b pb-8 md:flex-row md:items-end">
          <div>
            <p className="text-sm font-semibold text-primary-700">Votum Design System</p>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight text-content-primary sm:text-4xl">Componentes de interfaz</h1>
            <p className="mt-3 max-w-2xl text-content-muted">Biblioteca visual para construir una experiencia administrativa consistente, clara y accesible.</p>
          </div>
          <Button onClick={() => setModalOpen(true)}><Plus className="size-4" /> Nuevo registro</Button>
        </div>

        <div className="mt-10 space-y-12">
          <section>
            <SectionTitle eyebrow="Fundamentos" title="Acciones y estados" description="Jerarquías visuales para acciones, indicadores y mensajes." />
            <div className="grid gap-5 xl:grid-cols-2">
              <Card title="Botones" description="Variantes, tamaños y estados de carga.">
                <div className="flex flex-wrap items-center gap-3">
                  <Button>Primario</Button>
                  <Button variant="secondary">Secundario</Button>
                  <Button variant="outline">Contorno</Button>
                  <Button variant="ghost">Sutil</Button>
                  <Button variant="danger">Eliminar</Button>
                  <Button loading>Guardando</Button>
                  <Button disabled>Deshabilitado</Button>
                </div>
                <div className="mt-4 flex items-center gap-3">
                  <Button size="sm">Pequeño</Button>
                  <Button size="md">Mediano</Button>
                  <Button size="lg">Grande</Button>
                </div>
              </Card>
              <Card title="Badges y carga" description="Estados compactos para listas y procesos.">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge>Neutral</Badge>
                  <Badge variant="primary" dot>Principal</Badge>
                  <Badge variant="success" dot>Activo</Badge>
                  <Badge variant="warning" dot>Pendiente</Badge>
                  <Badge variant="danger" dot>Inválido</Badge>
                  <Badge variant="info" dot>Información</Badge>
                </div>
                <div className="mt-7 flex flex-wrap items-center gap-8">
                  <LoadingSpinner size="sm" label="Cargando" />
                  <LoadingSpinner size="md" label="Procesando datos" />
                  <LoadingSpinner size="lg" label="" />
                </div>
              </Card>
            </div>
          </section>

          <section>
            <SectionTitle eyebrow="Formularios" title="Controles de entrada" description="Preparados para React Hook Form, validaciones y estados deshabilitados." />
            <Card>
              <form onSubmit={handleSubmit(() => undefined)} className="grid gap-5 md:grid-cols-2">
                <Input id="name" label="Nombre completo" placeholder="Ej. Ana Martínez" required error={errors.name?.message} {...register('name', { required: 'El nombre es obligatorio' })} />
                <Input id="email" type="email" label="Correo electrónico" placeholder="nombre@votum.com" icon={Mail} hint="Se utilizará para enviar notificaciones." />
                <Select id="role" label="Rol" placeholder="Seleccionar un rol" options={[
                  { value: 'coordinator', label: 'Coordinador' },
                  { value: 'field-agent', label: 'Puntero' },
                  { value: 'voter', label: 'Votante' },
                ]} />
                <Input id="zone" label="Zona asignada" value="Distrito Centro" disabled readOnly />
                <Textarea id="notes" label="Observaciones" placeholder="Agregar información relevante..." className="md:col-span-2" />
                <div className="space-y-3">
                  <Checkbox id="mobile" label="Necesita móvil" description="Incluir en la planificación de traslados." defaultChecked />
                  <Checkbox id="notifications" label="Recibir notificaciones" />
                </div>
                <fieldset className="space-y-3">
                  <legend className="mb-2 text-sm font-medium text-content-primary">Estado inicial</legend>
                  <RadioButton id="active" name="status" value="active" label="Activo" defaultChecked />
                  <RadioButton id="pending" name="status" value="pending" label="Pendiente" />
                </fieldset>
                <div className="md:col-span-2">
                  <Button type="submit">Validar formulario</Button>
                </div>
              </form>
            </Card>
          </section>

          <section>
            <SectionTitle eyebrow="Datos" title="Búsqueda, tabla y paginación" description="Patrones para consultar estructuras y registros electorales." />
            <div className="mb-4 flex flex-col justify-between gap-3 sm:flex-row">
              <div className="w-full sm:max-w-sm">
                <SearchInput value={query} onChange={(event) => setQuery(event.target.value)} onClear={() => setQuery('')} placeholder="Buscar personas, roles o zonas..." aria-label="Buscar registros" />
              </div>
              <DropdownMenu
                label="Acciones"
                icon={Menu}
                items={[
                  { id: 'edit', label: 'Editar selección', icon: Edit3 },
                  { id: 'invite', label: 'Enviar invitación', icon: UserPlus },
                  { id: 'separator', separator: true },
                  { id: 'delete', label: 'Eliminar', icon: Trash2, danger: true, onClick: () => setConfirmOpen(true) },
                ]}
              />
            </div>
            <Table columns={columns} data={sortedPeople} sort={sort} onSort={handleSort} />
            <div className="mt-4">
              <Pagination page={page} totalPages={4} onPageChange={setPage} />
            </div>
          </section>

          <section>
            <SectionTitle eyebrow="Feedback" title="Alertas y estado vacío" />
            <div className="grid gap-5 lg:grid-cols-2">
              <div className="space-y-3">
                {alertVisible && <Alert variant="success" title="Cambios guardados" onClose={() => setAlertVisible(false)}>La información se actualizó correctamente.</Alert>}
                <Alert variant="info" title="Información">La sincronización del padrón estará disponible en una fase posterior.</Alert>
                <Alert variant="warning" title="Revisión necesaria">Hay 3 planillas pendientes de validación.</Alert>
                <Alert variant="danger" title="No se pudo completar">Verifica los datos e intenta nuevamente.</Alert>
              </div>
              <EmptyState icon={Users} title="No hay punteros registrados" description="Crea el primer puntero para comenzar a construir la estructura del coordinador." actionLabel="Crear puntero" onAction={() => setModalOpen(true)} />
            </div>
          </section>

          <section>
            <SectionTitle eyebrow="Navegación" title="Tabs, overlays y menús" />
            <Card>
              <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
              <div className="mt-3 flex flex-wrap gap-3 border-t pt-5">
                <Button variant="outline" onClick={() => setModalOpen(true)}>Abrir modal</Button>
                <Button variant="outline" onClick={() => setDrawerOpen(true)}>Abrir drawer</Button>
                <Button variant="danger" onClick={() => setConfirmOpen(true)}>Confirmar acción</Button>
                <Tooltip content="Información contextual sin interrumpir el flujo" position="top">
                  <Button variant="ghost">Ver tooltip</Button>
                </Tooltip>
              </div>
            </Card>
          </section>
        </div>
      </main>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Crear registro"
        description="Completa la información básica para continuar."
        footer={<><Button variant="ghost" onClick={() => setModalOpen(false)}>Cancelar</Button><Button onClick={() => setModalOpen(false)}><Check className="size-4" /> Guardar</Button></>}
      >
        <div className="space-y-4">
          <Input id="modal-name" label="Nombre completo" placeholder="Escribe un nombre" />
          <Select id="modal-role" label="Rol" placeholder="Seleccionar" options={[{ value: 'coordinator', label: 'Coordinador' }, { value: 'field-agent', label: 'Puntero' }]} />
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => setConfirmOpen(false)}
        title="Eliminar registro"
        description="Esta acción quitará el registro de la vista actual. ¿Deseas continuar?"
        confirmLabel="Sí, eliminar"
        danger
      />

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title="Detalle del coordinador"
        description="Información y actividad reciente."
        footer={<Button onClick={() => setDrawerOpen(false)}>Guardar cambios</Button>}
      >
        <div className="space-y-5">
          <div className="rounded-card bg-slate-50 p-4">
            <p className="text-sm font-semibold text-content-primary">Ana Martínez</p>
            <p className="mt-1 text-sm text-content-muted">Coordinadora · Distrito Centro</p>
          </div>
          <Input id="drawer-email" label="Correo electrónico" defaultValue="ana@votum.com" />
          <Select id="drawer-status" label="Estado" defaultValue="active" options={[{ value: 'active', label: 'Activo' }, { value: 'inactive', label: 'Inactivo' }]} />
          <Button variant="ghost" className="text-danger-600"><LogOut className="size-4" /> Revocar acceso</Button>
        </div>
      </Drawer>
    </div>
  );
}

export default ComponentsPage;
